/**
 * 智能客服流式 API。
 *
 * client.ts 的 `apiRequest` 只做 JSON 一问一答,这里单独写一个读 SSE 流的 fetch
 * (复用 auth token 与 `/api/v1` base)。后端 `/support/chat` 把 iris 的 OpenAI 兼容
 * SSE 原样透传过来:
 *   data: {"choices":[{"delta":{"content":"片段"}}]}
 *   data: [DONE]
 *
 * 多轮记忆靠 session_id:本端持久化一个稳定 id,iris 侧据它加载历史,故每次只发
 * 用户最新一条消息。
 */

import { useAuthStore } from '@/stores/auth'

// 默认相对 /api/v1(dev vite proxy / 3a Caddy 同源);Tauri 构建注入绝对地址
// (单机版 → 本机 sidecar;云版 → api.vip0101.com)。
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'
const SESSION_KEY = 'fabric.support_session'

function newSessionId(): string {
  // Tauri webview 是安全上下文,crypto.randomUUID 可用;留个兜底。
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

/** 取(或首次生成)本端稳定会话 id —— 多轮记忆靠它。 */
export function supportSessionId(): string {
  let sid = localStorage.getItem(SESSION_KEY)
  if (!sid) {
    sid = newSessionId()
    localStorage.setItem(SESSION_KEY, sid)
  }
  return sid
}

/** 开新对话:换 session id,iris 侧旧历史不再注入。返回新 id。 */
export function resetSupportSession(): string {
  const sid = newSessionId()
  localStorage.setItem(SESSION_KEY, sid)
  return sid
}

export interface StreamChatOptions {
  /** 用户本轮提问(最新一条)。 */
  message: string
  /** 会话 id(见 supportSessionId)。 */
  sessionId: string
  /** 取消用;关闭抽屉 / 开新对话时 abort 省 token。 */
  signal?: AbortSignal
  /** 每收到一段增量文本回调一次(累计拼接由调用方做)。 */
  onToken: (delta: string) => void
}

interface SseChunk {
  choices?: Array<{ delta?: { content?: string } }>
  error?: { message?: string }
}

/**
 * 向 `/support/chat` 发起流式提问,逐 token 回调。正常结束 resolve;鉴权失败 /
 * 客服不可用 / iris 报错则 reject(Error)。
 */
export async function streamChat(opts: StreamChatOptions): Promise<void> {
  return streamChatAt('/support/chat', opts)
}

/**
 * 通用流式聊天:把 message + session_id POST 到 `path`,逐 token 回调。
 * 客服(`/support/chat`)与电子宠物(`/pet/chat`)共用这套 SSE 解析。
 * 非 2xx → 抛 Error;错误体里的 `code` 挂到 `err.code`(如 pet 的 `pet_quota_exhausted`,
 * 供前端区分"额度用尽"走升级引导而非通用报错)。
 */
export async function streamChatAt(path: string, opts: StreamChatOptions): Promise<void> {
  const auth = useAuthStore()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`

  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message: opts.message, session_id: opts.sessionId }),
    signal: opts.signal
  })

  if (!resp.ok || !resp.body) {
    // 错误体是普通 JSON(后端 ApiError wire format),不是 SSE。
    // ⚠ wire 字段名是 `error`(fabric-server error.rs ApiErrorBody),不是 `code` —— 读错会让
    //   pet 配额耗尽(pet_quota_exhausted)/503/429 等按 code 的分流全部失效。
    let msg = `服务暂时不可用 (${resp.status})`
    let code: string | undefined
    try {
      const data = (await resp.json()) as { message?: string; error?: string }
      if (data?.message) msg = data.message
      code = data?.error
    } catch {
      /* 非 JSON,沿用默认文案 */
    }
    const err = new Error(msg) as Error & { code?: string }
    err.code = code
    throw err
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })

    // SSE 以行分隔;只处理完整行,末行可能被 chunk 切断 → 留到下次。
    const lines = buf.split('\n')
    buf = lines.pop() ?? ''
    for (const line of lines) {
      const t = line.trim()
      if (!t.startsWith('data:')) continue
      const payload = t.slice(5).trim()
      if (!payload || payload === '[DONE]') continue

      let chunk: SseChunk
      try {
        chunk = JSON.parse(payload) as SseChunk
      } catch {
        continue // 极少数分片不完整,跳过
      }
      if (chunk.error?.message) throw new Error(chunk.error.message)
      const delta = chunk.choices?.[0]?.delta?.content
      if (typeof delta === 'string' && delta) opts.onToken(delta)
    }
  }
}
